import React, { Fragment, useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, Transition } from '@headlessui/react';
import { THEME } from '../constants/theme';
import {
  HomeIcon,
  DocumentTextIcon,
  DocumentIcon,
  CreditCardIcon,
  UserGroupIcon,
  UserCircleIcon,
  ChevronDownIcon,
  ArrowRightOnRectangleIcon,
  Cog6ToothIcon,
  ChatBubbleLeftRightIcon
} from '@heroicons/react/24/outline';
import { supabase } from '../lib/supabase';
import NotificationsDropdown from './NotificationsDropdown';

const baseNavigation = [
  { name: 'Dashboard', href: '/dashboard', icon: HomeIcon },
  { name: 'Quotes', href: '/quotes', icon: DocumentTextIcon },
  { name: 'Invoices', href: '/invoices', icon: DocumentIcon },
  { name: 'Expenses', href: '/expenses', icon: CreditCardIcon },
  { name: 'Clients', href: '/clients', icon: UserGroupIcon },
];

const adminNavItem = { name: 'Service Desk', href: '/service-desk', icon: ChatBubbleLeftRightIcon };

export default function Navbar({ user, onSignOut }) {
  const location = useLocation();
  const [businessProfile, setBusinessProfile] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [navigation, setNavigation] = useState(baseNavigation);

  const checkAdminStatus = async () => {
    try {
      if (!user?.id) return;

      const { data: adminData } = await supabase
        .from('admins')
        .select('id')
        .eq('user_id', user.id)
        .single();

      setIsAdmin(!!adminData);
      if (adminData) {
        setNavigation([...baseNavigation, adminNavItem]);
      } else {
        setNavigation(baseNavigation);
      }
    } catch (error) {
      console.error('Error checking admin status:', error);
      setIsAdmin(false);
      setNavigation(baseNavigation);
    }
  };

  const fetchBusinessProfile = async () => {
    try {
      if (!user?.id) return;

      const { data, error } = await supabase
        .from('business_profile')
        .select('*')
        .eq('user_id', user.id)
        .single();

      if (error) throw error;
      setBusinessProfile(data);
    } catch (error) {
      console.error('Error fetching business profile:', error);
    }
  };

  useEffect(() => {
    if (user?.id) {
      checkAdminStatus();
      fetchBusinessProfile();
    }
  }, [user?.id]);

  return (
    <nav className="bg-white border-b border-gray-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16">
          <div className="flex">
            <div className="flex-shrink-0 flex items-center">
              <Link to="/dashboard" className="flex items-center" style={{ color: THEME.primary }}>
                <img src="/logo.png" alt="Aided" className="h-8" />
              </Link>
            </div>
            <div className="hidden sm:ml-6 sm:flex sm:space-x-8">
              {navigation.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`inline-flex items-center px-1 pt-1 text-sm font-medium border-b-2 ${
                    location.pathname === item.href
                      ? `border-${THEME.primary} text-${THEME.primary}`
                      : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
                  }`}
                >
                  <item.icon className="h-5 w-5 mr-1.5" />
                  {item.name}
                </Link>
              ))}
            </div>
          </div>
          
          <div className="flex items-center">
            {user && <NotificationsDropdown user={user} />}
            
            <Menu as="div" className="ml-3 relative">
              <div>
                <Menu.Button className="flex items-center max-w-xs text-sm rounded-full focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary">
                  {businessProfile?.logo_url ? <img src={businessProfile?.logo_url} alt="User Logo" className="h-8 w-8 rounded-full" /> : <UserCircleIcon className="h-8 w-8 text-gray-400" />}
                  <span className="ml-2 text-gray-700">{user?.email}</span>
                  <ChevronDownIcon className="ml-2 h-5 w-5 text-gray-400" />
                </Menu.Button>
              </div>
              <Transition
                as={Fragment}
                enter="transition ease-out duration-100"
                enterFrom="transform opacity-0 scale-95"
                enterTo="transform opacity-100 scale-100"
                leave="transition ease-in duration-75"
                leaveFrom="transform opacity-100 scale-100"
                leaveTo="transform opacity-0 scale-95"
              >
                <Menu.Items className="origin-top-right absolute right-0 mt-2 w-48 rounded-md shadow-lg py-1 bg-white ring-1 ring-black ring-opacity-5 focus:outline-none">
                  <Menu.Item>
                    {({ active }) => (
                      <Link
                        to="/profile"
                        className={`${
                          active ? 'bg-gray-100' : ''
                        } flex px-4 py-2 text-sm text-gray-700`}
                      >
                        <UserCircleIcon className="h-5 w-5 mr-2" />
                        Profile
                      </Link>
                    )}
                  </Menu.Item>
                  <Menu.Item>
                    {({ active }) => (
                      <button
                        onClick={onSignOut}
                        className={`${
                          active ? 'bg-gray-100' : ''
                        } flex w-full px-4 py-2 text-sm text-gray-700`}
                      >
                        <ArrowRightOnRectangleIcon className="h-5 w-5 mr-2" />
                        Sign Out
                      </button>
                    )}
                  </Menu.Item>
                </Menu.Items>
              </Transition>
            </Menu>
          </div>
        </div>
      </div>
    </nav>
  );
} 